import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";

const KEY = "ue24_consent";

// Consent Mode v2: gespeicherte/aktuelle Wahl an Google weitergeben.
// gtag.js selbst lädt zentral im <head> der index.html – dort stehen die
// Defaults (alles „denied"), hier kommt nur noch das „update".
const applyConsent = (granted) => {
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
  const state = granted ? "granted" : "denied";
  window.gtag("consent", "update", {
    ad_storage: state,
    ad_user_data: state,
    ad_personalization: state,
    analytics_storage: state,
  });
};

// Erst nach Einwilligung: GTM + optionale GA4-Config (gtag.js ist bereits geladen)
const loadMarketing = () => {
  applyConsent(true);
  if (!document.querySelector('script[src*="googletagmanager.com/gtm.js"]')) {
    window.dataLayer.push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
    const g = document.createElement("script");
    g.async = true;
    g.src = "https://www.googletagmanager.com/gtm.js?id=GTM-WSKX8ZZ";
    document.head.appendChild(g);
  }
  const ga4 = process.env.REACT_APP_GA4_ID;
  if (ga4) window.gtag("config", ga4);
};

export const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [view, setView] = useState("main");
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    // Beim Pre-Rendering (headless Chromium) den Banner nicht ins statische
    // HTML schreiben – sonst Hydration-Mismatch für Besucher mit gespeicherter
    // Einwilligung. Im echten Browser erscheint er nach der Hydration.
    if (navigator.userAgent.includes("HeadlessChrome")) return;
    setMounted(true);
    const consent = localStorage.getItem(KEY);
    // Gespeicherte Zustimmung bei jedem Seitenaufruf sofort wieder anwenden
    if (consent === "accepted") loadMarketing();
    else if (consent === "declined") applyConsent(false);
    else setVisible(true);
    // Wiederaufruf über „Cookie-Einstellungen" (Footer, Cookie-Richtlinie)
    window.__openCookieSettings = () => {
      setMarketing(localStorage.getItem(KEY) === "accepted");
      setView("settings");
      setVisible(true);
    };
  }, []);

  const decide = (accepted) => {
    localStorage.setItem(KEY, accepted ? "accepted" : "declined");
    if (accepted) loadMarketing();
    else applyConsent(false);
    window.dispatchEvent(new Event("ue24-consent-changed"));
    setVisible(false);
  };

  if (!mounted || !visible) return null;

  // Gleich gewichtete Buttons (DSGVO): kein hervorgehobener „Akzeptieren"-Button
  const btnCls = "px-4 py-2.5 rounded-lg border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-100 transition-colors";

  return (
    <div className="fixed bottom-0 inset-x-0 z-[60] p-4 md:bottom-6 md:left-6 md:right-auto md:max-w-md" data-testid="cookie-banner">
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 p-5">
        {view === "main" ? (
          <>
            <div className="flex items-center gap-2 font-bold text-navy"><Cookie size={18} className="text-ember" /> Cookies & Datenschutz</div>
            <p className="text-xs text-slate-500 mt-2 leading-relaxed">
              Wir nutzen Cookies, um unsere Website optimal zu gestalten und die Zugriffe zu analysieren. Sie entscheiden, ob Sie zustimmen. Details in der <Link to="/datenschutzerklaerung" className="underline">Datenschutzerklärung</Link>.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 mt-4">
              <button onClick={() => decide(false)} data-testid="cookie-decline-button" className={btnCls}>
                Nur notwendige
              </button>
              <button onClick={() => setView("settings")} data-testid="cookie-settings-button" className={btnCls}>
                Einstellungen
              </button>
              <button onClick={() => decide(true)} data-testid="cookie-accept-button" className={btnCls}>
                Alle akzeptieren
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2 font-bold text-navy"><Cookie size={18} className="text-ember" /> Cookie-Einstellungen</div>
            <div className="mt-4 space-y-3">
              <div className="flex items-start justify-between gap-3 border border-slate-200 rounded-lg p-3">
                <div>
                  <p className="text-sm font-bold text-navy">Notwendig</p>
                  <p className="text-xs text-slate-500 mt-0.5">Technisch erforderlich für den Betrieb der Website – immer aktiv.</p>
                </div>
                <input type="checkbox" checked disabled data-testid="cookie-cat-necessary" className="mt-1 h-4 w-4 accent-[#4A55B8]" />
              </div>
              <div className="flex items-start justify-between gap-3 border border-slate-200 rounded-lg p-3">
                <div>
                  <p className="text-sm font-bold text-navy">Marketing / Statistik</p>
                  <p className="text-xs text-slate-500 mt-0.5">Google-Ads-Conversion-Messung und Reichweiten-Analyse (GA4, Meta Pixel). Kein vorab gesetztes Häkchen.</p>
                </div>
                <input type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} data-testid="cookie-cat-marketing" className="mt-1 h-4 w-4 accent-[#4A55B8]" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2.5 mt-4">
              <button onClick={() => decide(marketing)} data-testid="cookie-save-button" className={btnCls}>
                Auswahl speichern
              </button>
              <button onClick={() => decide(true)} data-testid="cookie-accept-all-button" className={btnCls}>
                Alle akzeptieren
              </button>
            </div>
            <button onClick={() => setView("main")} data-testid="cookie-back-button" className="mt-2 w-full text-center text-xs text-slate-400 hover:text-ember py-1">
              Zurück
            </button>
          </>
        )}
      </div>
    </div>
  );
};
